import React from 'react';

const CheckIcon = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M6.6 11.1L3.9 8.4a1 1 0 10-1.4 1.4l3.4 3.4a1 1 0 001.4 0l6.8-6.8a1 1 0 10-1.4-1.4l-6.1 6.1z"
      fill="currentColor"
    />
  </svg>
);

const CrossIcon = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M4.2 4.2a1 1 0 011.4 0L8 6.6l2.4-2.4a1 1 0 111.4 1.4L9.4 8l2.4 2.4a1 1 0 01-1.4 1.4L8 9.4l-2.4 2.4a1 1 0 01-1.4-1.4L6.6 8 4.2 5.6a1 1 0 010-1.4z"
      fill="red"
    />
  </svg>
);

const Bullet = ({ children, included = true }) => (
  <li className={`billing-bullet ${included ? '' : 'billing-bullet--excluded'}`}>
    <span className="billing-bullet__icon">
      {included ? <CheckIcon /> : <CrossIcon />}
    </span>
    <span className="billing-bullet__text">{children}</span>
  </li>
);

export default function PlanCard({
  title,
  subtitle,
  pricePrimary,
  priceStrikethrough,
  priceSecondary,
  bullets,
  cta,
  onClick,
  disabled,
  current,
  featured,
  footnote,
  currentDetails,
}) {
  return (
    <div className={`billing-plan card h-100 ${featured ? 'billing-plan--featured' : ''}`}>
      <div className="card-body d-flex flex-column p-3">
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0">{title}</h5>
              {featured && !currentDetails ? <span className="badge rounded-pill text-bg-dark">Most popular</span> : null}
              {current ? <span className="badge rounded-pill text-bg-success">Current</span> : null}
            </div>
            {subtitle ? <div className="text-muted mt-1" style={{ fontSize: 13 }}>{subtitle}</div> : null}
          </div>
        </div>

        <div className="mt-2">
          <div className="billing-price">
            <span className="billing-price__primary">{pricePrimary}</span>
            {priceStrikethrough ? <span className="billing-price__strikethrough">{priceStrikethrough}</span> : null}
          </div>
          {priceSecondary ? <div className="billing-price__secondary">{priceSecondary}</div> : null}
        </div>

        <ul className="billing-bullets mt-2 mb-3">
          {(bullets || []).map((b, idx) => {
            const item = typeof b === 'string' ? { text: b, included: true } : b;
            const text = item?.text || '';
            return (
              <Bullet key={`${text}-${idx}`} included={item?.included !== false}>
                {text}
              </Bullet>
            );
          })}
        </ul>

        {currentDetails && (
          <>
            <hr className="my-3" />
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="billing-kpi__label">Status</span>
              <span className={`badge rounded-pill ${currentDetails.statusClass}`}>{currentDetails.statusLabel}</span>
            </div>
            {currentDetails.periodEnd && (
              <div className="billing-kpi__label mb-2">
                Renews <span className="fw-semibold">{currentDetails.periodEnd}</span>
              </div>
            )}
            {currentDetails.assessmentUsed != null && currentDetails.assessmentLimit != null && currentDetails.assessmentLimit > 0 && (
              <div className="mb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="billing-kpi__label">Assessments</div>
                  <div className="billing-kpi__value">{currentDetails.assessmentUsed} / {currentDetails.assessmentLimit}</div>
                </div>
                <div className="progress billing-progress mt-2" role="progressbar" aria-valuenow={currentDetails.assessmentPercent} aria-valuemin="0" aria-valuemax="100">
                  <div className="progress-bar" style={{ width: `${currentDetails.assessmentPercent}%` }} />
                </div>
              </div>
            )}
            {currentDetails.domainLimit != null && (
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="billing-kpi__label">Widget domains</div>
                <div className="billing-kpi__value">{currentDetails.domainLimit}</div>
              </div>
            )}
            <div className="d-flex flex-column gap-1 mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="billing-kpi__label">PDF downloads</div>
                <span className={`badge rounded-pill ${currentDetails.canDownloadPdf ? 'text-bg-success' : 'text-bg-light'}`}>
                  {currentDetails.canDownloadPdf ? 'Included' : 'Not included'}
                </span>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <div className="billing-kpi__label">Methodology</div>
                <span className={`badge rounded-pill ${currentDetails.canViewMethodology ? 'text-bg-success' : 'text-bg-light'}`}>
                  {currentDetails.canViewMethodology ? 'Included' : 'Not included'}
                </span>
              </div>
            </div>
          </>
        )}

        <button
          className="btn btn-outline-primary mt-auto billing-cta"
          onClick={onClick}
          disabled={disabled}
        >
          {cta}
        </button>
        {footnote ? <div className="text-muted mt-2" style={{ fontSize: 12 }}>{footnote}</div> : null}
      </div>
    </div>
  );
}
